/**
 * Mapping of parent entity types to their possible subentity types
 * Based on IWithEmbeddedSubentities interface implementations in the backend
 */

import { EntityType, entityTypeLabelMap } from '../../entities/common';

/**
 * All parent/top-level entity types that can be used in audit log filtering
 * Includes both entities that can have subentities and standalone entities
 * Order determines display order in UI
 */
export const PARENT_ENTITY_TYPES = [
    EntityType.Project,
    EntityType.Portfolio,
    EntityType.Program,
    EntityType.Roadmap,
    EntityType.Idea,
    EntityType.Challenge,
    EntityType.Objective,
    EntityType.Resource,
    EntityType.ArchivedProject,
    EntityType.User,
] as const;

/**
 * Map of parent entities to their possible subentity types
 * Only includes entities that actually have subentities
 */
export const ENTITY_SUBENTITY_MAP = {
    [EntityType.Project]: [
        EntityType.KeyDate,
        EntityType.Risk,
        EntityType.Issue,
        EntityType.Iteration,
        EntityType.LessonLearned,
        EntityType.ActionItem,
        EntityType.KeyDecision,
        EntityType.SteeringCommittee,
        EntityType.Deliverable,
        EntityType.ChangeRequest,
        EntityType.Dependency,
        EntityType.PurchaseOrder,
        EntityType.Invoice,
        EntityType.Task
    ],
    [EntityType.Portfolio]: [
        EntityType.KeyDate,
        EntityType.ActionItem,
        EntityType.KeyDecision,
        EntityType.Issue,
        EntityType.ChangeRequest,
        EntityType.Risk
    ],
    [EntityType.Program]: [
        EntityType.KeyDate,
        EntityType.ActionItem,
        EntityType.KeyDecision,
        EntityType.Issue,
        EntityType.ChangeRequest,
        EntityType.Risk
    ],
    [EntityType.Roadmap]: [
        EntityType.RoadmapItem
    ],
    [EntityType.Idea]: [
        EntityType.Risk
    ],
} as const;

/**
 * Get subentity types for a given parent entity type
 * Case-insensitive to handle URL parameters that may be in any case
 */
export function getSubentitiesForEntity(entityType: string): EntityType[] {
    if (!entityType) {
        return [];
    }
    
    // Convert to lowercase and lookup directly (all keys are lowercase)
    const lowerType = entityType.toLowerCase() as EntityType;
    return ENTITY_SUBENTITY_MAP[lowerType] || [];
}

/**
 * Check if an entity type can have subentities
 * Case-insensitive to handle URL parameters that may be in any case
 */
export function canHaveSubentities(entityType: string): boolean {
    if (!entityType) {
        return false;
    }

    // Convert to lowercase and check directly (all keys are lowercase)
    return entityType.toLowerCase() in ENTITY_SUBENTITY_MAP;
}

/**
 * Get display label for entity type using the common entityTypeLabelMap
 */
export const getLabelForEntityType = (entityType: string): string => {
    if (!entityType) {
        return '';
    }
    const lowerType = entityType.toLowerCase() as EntityType;
    return entityTypeLabelMap[lowerType]?.singular ?? entityType;
};
